import { Box, Grid, SelectChangeEvent, TextField } from '@mui/material'
import { format, isEqual, isValid, isWeekend } from 'date-fns'
import { useState, useEffect, useCallback, useRef, useMemo, ChangeEvent } from 'react'
import { useSelector } from 'react-redux'
import { DateRange, NonEmptyDateRange } from '@mui/x-date-pickers-pro-7'
import { useSearchParams } from 'react-router-dom'
import debounce from 'lodash.debounce'
import { RequestType } from '../../models/request-type'
import { hideModal, hideDrawer, hideHeaderDrawer } from '../../redux/reducers/appSettingsReducer'
import { showErrorMessage, showSuccessMessage } from '../../redux/reducers/snackbarReducer'
import { setAlerts, setSelectedDays } from '../../redux/reducers/timeOffRequestsReducer'
import { RootStore, useAppDispatch } from '../../redux/store'
import { Employee, SelectOption } from '../../services/dashboardService'
import { requestsService } from '../../services/requestsService'
import { AbsenceLocation } from '../../types/absence-location'
import AutocompleteList from '../../shared/UI/AutocompleteList'
import Button from '../../shared/UI/Button'
import DrawerFooter from '../../shared/UI/DrawerFooter'
import ConfirmationCard, { ConfirmationRow } from './ConfirmationCard'
import Modal from '../../shared/UI/Modal'
import Paragraph from '../../shared/UI/Paragraph'
import { BaseResponse, ConflictMatch } from '../../types/base-response'
import {
  AbsenceCreateRequest,
  AbsenceOccurrence,
  WFMAbsenceActionedPayload,
} from '../../types/absence'
import { getSelectValuesByType } from '../../utils/app-utils'
import {
  CLASH_OVERRIDE_MESSAGE,
  REQUIRED_MESSAGE,
  submitCancelButtons,
  absenceFormLabels,
  absenceFormModalClash,
} from '../../utils/constants'
import {
  currentDayMidnight,
  formatDateWithTimeZone,
  formatDatTimeWithTimeZone,
  getLocalDateString,
  isValidPlannerDate,
  isValidPlannerDateRange,
  nextWorkingDay,
  toMidnight,
} from '../../utils/date-utils'
import { useForm } from '../../utils/useForm'
import MultiDayRequest from '../MultiDayRequest'
import {
  absenceComponentValidationSchema,
  AbsenceDrawerValidationErrors,
  AbsenceRequestModel,
  absenceRequestModelDefaultState,
} from './model'
import { manualRequestsService } from '../../services/myActionsService'
import UserErrorMessage from '../../utils/errorFilter'
import RequestRangeConflict from '../../types/request-range-conflict'
import Alert from '../../shared/UI/Alert/Alert'
import { stringifyDaysArray } from '../../utils/conversion'
import { calculatePartDayAbsence, getValidationErrorMessages } from './AbsenceDrawerFunctions'
import { LocationGroups } from '../../types/location-groups'
import UKAbsenceForm from './LocationAbsenceForm/ukAbsenceForm'
import AbsenceForm from './LocationAbsenceForm/absenceForm'
import { settingsService } from '../../services/settingsService'
import useWfmAbsence from './WfmAbsenceDrawer/Hooks/useWfmAbsence'
import actionsService from '../../services/actionsService'
import { DateFormats } from '../../api/absence/tempTypes/generalprops'
import { AbsenceReasonTypesEnum, AbsenceTypesEnum } from '../Absence/types'
import {
  calculateManAuthDays,
  getOSPForAbsenceType,
  isAbsenceDurationExceeded,
  isAbsenceDurationExceededWithExtension,
  AbsenceValidationResult,
  buildPayApprovalText,
} from './utils'
import { AbsenceFormProps } from './LocationAbsenceForm/types'
import { featureToggleKeys } from '../../types/feature-toggles'
import { useManAuth } from '../Absence/Actions/RTW/UseManAuth'
import { useDepartmentSettings } from '../../utils/Hooks/useDepartmentSettings'
import { useIsSplitPotsForUk } from '../../utils/Hooks/useIsSplitPotsForUk'
import FormValidationErrorMessage from '../../shared/UI/FormValidationErrorMessage'
import { useShiftLength } from './hooks/index'
import { useOSPCalculations } from './hooks/useOSPCalculations'
import { useManAuthValidation } from './hooks/useManAuthValidation'
import { useSickNoteReminder } from './hooks/useSickNoteReminder'
import { useAbsenceOverview } from './hooks/useAbsenceOverview'
import { useAbsenceLedger } from './hooks/useAbsenceLedger'

type Props = {
  extendAbsenceData?: AbsenceOccurrence
  onAbsenceSubmit?: () => void
}

type ShiftFetchParams = {
  dateRange: DateRange<Date>
  employeeId: number
}


export const createAbsentFromDate = (shiftDate: Date): Date => {
  const newShiftDate = new Date(shiftDate)
  const now = new Date()
  const newAbsentFrom = new Date(
    newShiftDate.getFullYear(),
    newShiftDate.getMonth(),
    newShiftDate.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds()
  )

  return newAbsentFrom
}

export const handleAbsentFromChanges = (
  isOpen: boolean,
  absentFromDate: Date | undefined,
  shiftDate: Date,
  setAbsentFromDate: (value: Date | undefined) => void,
  absenceData: AbsenceRequestModel,
  setData: (value: AbsenceRequestModel) => void
): void => {
  if (isOpen && !absentFromDate) {
    const newAbsentFrom = createAbsentFromDate(shiftDate)
    setData({ ...absenceData, absentFrom: newAbsentFrom })
    setAbsentFromDate(newAbsentFrom)
  }

  if (!isOpen && absentFromDate) {
    setAbsentFromDate(undefined)
  }
}

const defaultHasHadLunch = false

export const DiscretionaryPayReason = [
  AbsenceReasonTypesEnum.PartnerChildDependantNotWell,
  AbsenceReasonTypesEnum.Compassionate,
  AbsenceReasonTypesEnum.Other,
]

function AbsenceDrawerComponent({ extendAbsenceData, onAbsenceSubmit }: Props) {
  const dispatch = useAppDispatch()

  const [currentTypeAssociatedValueId, setCurrentTypeAssociatedValueId] = useState<number>(
    extendAbsenceData?.absenceDays?.[0]?.absenceType?.reasonTypeId || 0
  )
  const [validationErrors, setValidationErrors] = useState<AbsenceDrawerValidationErrors>({})
  const { showModal, title, message, type, buttonLabel } = useSelector(
    (state: RootStore) => state.appSettings.modalProps
  )
  const { territoryAttendanceUK, ukPlannerDayforceAbsence, splitPots } = useSelector((state: RootStore) => state.featureToggles)
  const { selectedDays } = useSelector((state: RootStore) => state.timeOff)
  const { directReports } = useSelector((state: RootStore) => state.appSettings)
  const [open, setOpen] = useState(false)
  const [isEmployeeSelected, setIsEmployeeSelected] = useState<boolean>(false)

  const workBackEligibleLocations = useMemo(() => new Set(['Stoke', 'Manchester', 'Remote UK']), [])
  const [employeeAbsenceLocations, setEmployeeAbsenceLocations] = useState<AbsenceLocation[]>([])
  const [isLoadingEmployeeAbsenceLocations, setIsLoadingEmployeeAbsenceLocations] = useState<boolean>(false)
  const [selectedEmployeeLocationId, setSelectedEmployeeLocationId] = useState<number | null>(null)
  const [absenceReasonTypeList, setAbsenceReasonTypeList] = useState<SelectOption[]>([])
  const [absenceTypesList, setAbsenceTypesList] = useState<SelectOption[]>([])
  const [hasConflict, setHasConflict] = useState<boolean>()
  const [showClashModal, setShowClashModal] = useState<boolean>(false)
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)
  const [isDisableShiftDescription, setIsDisableShiftDescription] = useState<boolean>(false)
  const [absentFromDate, setAbsentFromDate] = useState<Date>()
  const [absentFromChangeCounter, setAbsentFromChangeCounter] = useState<number>(0)
  const savedAbsentFromTimeRef = useRef<Date | undefined>(undefined)
  const hasRestoredTimeRef = useRef<boolean>(false)
  const [selectedEmployeeLocationName, setSelectedEmployeeLocationName] = useState<string>('')
  const [selectedEmployeeDepartmentId, setSelectedEmployeeDepartmentId] =
    useState<number | null>(null)
  const [employeeHandbookUrl, setEmployeeHandbookUrl] = useState<string | undefined>(undefined)
  const [departmentWorksWeekends, setDepartmentWorksWeekends] = useState<boolean>(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [wfmAbsenceId, setWfmAbsenceId] = useState<number | undefined>(undefined)
  const { wfmAbsence } = useWfmAbsence(wfmAbsenceId)
  const userInfo = useSelector((state: RootStore) => state.userState.loggedInUser)

  const [isDisableDateRange, setIsDisableDateRange] = useState<boolean>(false)
  const [disabledPartDayAbsence, setDisabledPartDayAbsence] = useState<boolean>(false)
  const [disabledAbsentFrom, setDisabledAbsentFrom] = useState<boolean>(false)

  const [hasFutureSicknessAbsence, setHasFutureSicknessAbsence] = useState<boolean>(false)
  const [hasFutureSicknessAfterEnd, setHasFutureSicknessAfterEnd] = useState<boolean>(false)
  const [expectedReturnDateError, setExpectedReturnDateError] = useState<boolean>(false)
  const [dateRangeError, setDateRangeError] = useState<boolean>(false)
  const [contactDateTimeError, setContactDateTimeError] = useState<boolean>(false)
  const [absentFromDateError, setAbsenceFromDateError] = useState<boolean>(false)

  const departmentSettings = useDepartmentSettings(selectedEmployeeDepartmentId)
  const { isManAuthOn } = useManAuth(featureToggleKeys.ManAuthUK, departmentSettings)

  const [dateRange, setDateRange] = useState<DateRange<Date>>(
    extendAbsenceData?.endDate
      ? [
          toMidnight(nextWorkingDay(
            extendAbsenceData?.endDate,
            Boolean(departmentSettings?.areWeekendsWorked)
          )),
          toMidnight(nextWorkingDay(
            extendAbsenceData?.endDate,
            Boolean(departmentSettings?.areWeekendsWorked)
          )),
        ]
      : [currentDayMidnight(), currentDayMidnight()]
  )
  const lastShiftFetchParameters = useRef<ShiftFetchParams | null>(null)

  useEffect(() => {
    if (extendAbsenceData && departmentSettings) {
      setDateRange([
        toMidnight(nextWorkingDay(extendAbsenceData?.endDate, departmentSettings.areWeekendsWorked)),
        toMidnight(nextWorkingDay(extendAbsenceData?.endDate, departmentSettings.areWeekendsWorked)),
      ])
    }
  }, [extendAbsenceData, departmentSettings])

  const handleDateRangeError = (hasError: boolean) => {
    setDateRangeError(hasError)
  }

  const { allEmployees } = useSelector((state: RootStore) => state.appSettings)

  const establishData = () => {
    let localData
    if (extendAbsenceData && dateRange[1] !== null) {
      localData = {
        absenceId: extendAbsenceData.id,
        employeeId: extendAbsenceData.employeeResponse.id,
        employeeName: extendAbsenceData.employeeResponse.displayName,
        absenceDate: new Date(extendAbsenceData.startDate),
        absentFrom: new Date(extendAbsenceData.startDate),
        endDate: new Date(extendAbsenceData.endDate),
        absenceTypeId: extendAbsenceData?.absenceDays?.[0]?.absenceType?.absenceTypeId || 0,
        absenceCategoryId: extendAbsenceData?.absenceDays?.[0]?.absenceType?.reasonTypeId || 0,
        shiftDescription: extendAbsenceData?.absenceDays?.[0]?.expectedShift?.shiftString || '',
        comments: extendAbsenceData.absenceDays[0].comments || '',
        contactDateTime: extendAbsenceData.absenceDays[0].contactDateTime,
        contactedByTypeId: extendAbsenceData.absenceDays[0].contactedByTypeId,
        contactComments: extendAbsenceData.absenceDays[0].contactComments || '',
        expectedReturn: nextWorkingDay(
          dateRange[1],
          Boolean(departmentSettings?.areWeekendsWorked)
        ),
        partDayAbsence: false,
        toWorkBack: extendAbsenceData.absenceDays[0].toWorkBack || false,
        sendFdn: false,
        hasHadLunch: defaultHasHadLunch,
        days: extendAbsenceData.absenceDays.map(day => ({
          checked: true,
          day: day.date,
          hours: day.hoursLost,
        })),
        startTime: extendAbsenceData?.absenceDays?.[0]?.expectedShift?.shiftDate || new Date(),
      }

      return localData
    }
    localData = absenceRequestModelDefaultState

    return localData
  }

  const form = useForm<AbsenceRequestModel>({
    initialValues: establishData(),

    validations: {
      ...absenceComponentValidationSchema,
      days: {
        custom: {
          isValid: (value: any) =>
            value && value.filter((day: any) => day.checked && day.hours <= 0).length === 0,
          message: REQUIRED_MESSAGE,
        },
      },
      contactDateTime: {
        custom: {
          isValid: (value: any): boolean => {
            // If no contact method is selected, contact date time is not required
            if (form.data.contactedByTypeId === 0) {
              return true
            }
            // If contact method is selected, contact date time must be present and valid
            if (form.data.contactedByTypeId !== 0) {
              return value !== null && value !== undefined && isValid(value)
            }
            return false
          },
          message: 'Contact date and time is required and must be valid when a contact method is selected',
        },
      },
      absentFrom: {
        custom: {
          isValid: value => {
            if (!form.data.partDayAbsence) {
              return true
            }
            if (form.data.partDayAbsence && value !== null) {
              return true
            }
            return false
          },
          message: REQUIRED_MESSAGE,
        },
      },
    },
  })

  const {
    handleSubmit,
    handleDateChange: formHandleDateChange,
    data: absenceData,
    setData,
    handleCheckboxChange,
    updateState,
  } = form

  const handleDateChange = useCallback((selectedDate: Date | null, name: string, keyboardInputValue?: string) => {
    formHandleDateChange(selectedDate, name, keyboardInputValue)
    if (name === 'absentFrom') {
      setAbsentFromChangeCounter(prev => prev + 1)
      if (selectedDate) {
        savedAbsentFromTimeRef.current = selectedDate
      }
    }
  }, [formHandleDateChange])

  const {
    remainingOSP,
    totalSickPayAllowanceHours,
    isLoadingOSP,
    dailyOSP,
    totalAvailableOSP,
    totalRequestedHours,
    exceededHours,
    exceededHoursPerDay,
  } = useOSPCalculations({
    employeeId: absenceData.employeeId,
    dateRange,
    selectedDays,
    absenceCategoryId: absenceData.absenceCategoryId,
  })

  const isSplitPotsForUk = useIsSplitPotsForUk(selectedEmployeeLocationName)

  const {
    absenceOverviewData,
    isLoading: isLoadingAbsenceOverview,
    ospBalances,
  } = useAbsenceOverview({
    employeeId: absenceData.employeeId,
    dateRange,
    selectedDays,
    absenceCategoryId: absenceData.absenceCategoryId,
    extendAbsenceData,
    enabled: isSplitPotsForUk,
  })

  const hasWorkBackDays = useMemo(() => {
    if (!extendAbsenceData?.absenceDays) return false
    return extendAbsenceData.absenceDays.some(day => day.toWorkBack === true)
  }, [extendAbsenceData?.absenceDays])

  const [managerApprovedHoursState, setManagerApprovedHoursState] = useState<number | undefined>(undefined)

  const {
    overviewRows: ledgerOverviewRows,
    breakdownRows: ledgerBreakdownRows,
    totalUnpaidBeforeDP: ledgerTotalUnpaidBeforeDP,
    unpaidPerDayBeforeDP: ledgerUnpaidPerDayBeforeDP,
    isLoading: isLoadingLedger,
  } = useAbsenceLedger({
    employeeId: absenceData.employeeId,
    dateRange,
    enabled: isSplitPotsForUk && absenceData.absenceCategoryId === 1,
    extendAbsenceData,
    selectedDays,
    discretionaryPayHours: managerApprovedHoursState,
  })

  const isSickness = absenceData.absenceCategoryId === 1
  const manAuthExceededHours = splitPots && isSickness ? ledgerTotalUnpaidBeforeDP : exceededHours

  const {
    isPayApprovalConfirmed,
    managerApprovedHours,
    hasValidHoursInput,
    isManAuthRequest,
    isManAuthValid,
    isManAuthValidWithHours,
    handleApprovalHoursChange,
    handlePayApprovalChange,
    setIsPayApprovalConfirmed,
  } = useManAuthValidation({
    isManAuthOn,
    absenceCategoryId: absenceData.absenceCategoryId,
    absenceTypeId: absenceData.absenceTypeId,
    exceededHours: manAuthExceededHours,
    toWorkBack: absenceData.toWorkBack,
    hasWorkBackDays,
    discretionaryPayReasons: DiscretionaryPayReason,
    externalManagerApprovedHours: splitPots ? managerApprovedHoursState : undefined,
    onManagerApprovedHoursChange: splitPots ? setManagerApprovedHoursState : undefined,
  })

  const enhancedAbsenceOverviewData = useMemo(() => {
    if (!absenceOverviewData) return absenceOverviewData
    if (!splitPots || !isSickness) return absenceOverviewData
    const ledgerOverview = ledgerOverviewRows[0]
    return {
      ...absenceOverviewData,
      ...(ledgerOverview && {
        paidHours: ledgerOverview.hundredPercentUsed,
        fiftyPercentUsed: ledgerOverview.fiftyPercentUsed,
      }),
      unpaidHours: ledgerTotalUnpaidBeforeDP,
      exceededHours: ledgerTotalUnpaidBeforeDP,
    }
  }, [absenceOverviewData, splitPots, isSickness, ledgerOverviewRows, ledgerTotalUnpaidBeforeDP])

  const [isWorkBackConfirmed, setIsWorkBackConfirmed] = useState<boolean>(false)
  const [isSickNoteConfirmed, setIsSickNoteConfirmed] = useState<boolean>(false)

  useEffect(() => {
    if (!ukPlannerDayforceAbsence) {
      return
    }

    if (!absenceData.toWorkBack && isWorkBackConfirmed) {
      setIsWorkBackConfirmed(false)
    }
  }, [absenceData.toWorkBack, isWorkBackConfirmed, ukPlannerDayforceAbsence])

  const handleWorkBackConfirmationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsWorkBackConfirmed(event.target.checked)
  }

  const handleSickNoteConfirmationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSickNoteConfirmed(event.target.checked)
  }

  const popperText = useMemo(
    () => (
      <>
        <Paragraph>Use this option to record the time the absence started.</Paragraph>
      </>
    ),
    []
  )

  const { alerts } = useSelector((state: RootStore) => state.timeOff)

  const gridContainerRef = useRef(null)

  useEffect(() => {
    async function onLoad() {
      setAbsenceReasonTypeList(getSelectValuesByType('AbsenceReasonType'))
      setAbsenceTypesList(getSelectValuesByType('AbsenceType'))

      setIsLoadingEmployeeAbsenceLocations(true)
      settingsService
        .getLocation()
        .then(res => {
          setEmployeeAbsenceLocations(res.absenceLocations || [])
        })
        .catch(() => {
          setEmployeeAbsenceLocations([])
        })
        .finally(() => setIsLoadingEmployeeAbsenceLocations(false))

      if (extendAbsenceData) {
        setIsEmployeeSelected(true)
        setSelectedEmployeeLocationName(extendAbsenceData.employeeResponse.locationGroupName || '')
        setSelectedEmployeeDepartmentId(extendAbsenceData.departmentId)
        setSelectedEmployeeLocationId(extendAbsenceData.employeeResponse.locationId || null)
        await settingsService.getSettings(extendAbsenceData.departmentId).then(res => {
          setDepartmentWorksWeekends(res.areWeekendsWorked)
          if (!res.areWeekendsWorked) {
            return
          }
          setDateRange([
            nextWorkingDay(extendAbsenceData?.endDate, res.areWeekendsWorked),
            nextWorkingDay(extendAbsenceData?.endDate, res.areWeekendsWorked),
          ])
        })
      }

      const absenceDayId = searchParams.get('wfmId')
      if (absenceDayId !== null && !Number.isNaN(Number(absenceDayId))) {
        setWfmAbsenceId(Number(absenceDayId))
      }

      setIsLoaded(true)
    }

    onLoad()
  }, [])

   const { shiftData, setShiftData, getShiftLength } = useShiftLength()

  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement & HTMLSelectElement>
      | SelectChangeEvent<string>
      | SelectChangeEvent<number>
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.name === 'absenceTypeId') {
      setIsPayApprovalConfirmed(false)
    }
    form.handleChange(e)
  }

  useEffect(() => {
    if (!dateRange || !dateRange[0]) {
      return
    }

    manualRequestsService.hasFutureSicknessAbsence(absenceData.employeeId, dateRange[0])
      .then(response => {
        setHasFutureSicknessAbsence(response.hasFutureSicknessAbsence)
      })
      .catch(err => {
        const response: BaseResponse = err.response.data
        response.errors.forEach(error => {
          dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
        })
      })
  }, [absenceData?.employeeId, dateRange[0]])

  useEffect(() => {
    if (!dateRange || !dateRange[1]) {
      return
    }

    manualRequestsService.hasFutureSicknessAbsence(absenceData.employeeId, dateRange[1])
      .then(response => {
        setHasFutureSicknessAfterEnd(response.hasFutureSicknessAbsence)
      })
      .catch(err => {
        const response: BaseResponse = err.response.data
        response.errors.forEach(error => {
          dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
        })
      })
  }, [absenceData?.employeeId, dateRange[1]])

  useEffect(() => {
    if (!wfmAbsence) {
      return
    }

    setIsDisableShiftDescription(true)
    setIsDisableDateRange(true)
    setDisabledPartDayAbsence(true)
    setDisabledAbsentFrom(true)
    
    setAbsentFromDate(formatDateWithTimeZone(wfmAbsence.absenceStartDate))
    
    setDateRange([
      formatDateWithTimeZone(wfmAbsence.absenceStartDate),
      formatDateWithTimeZone(wfmAbsence.absenceEndDate),
    ])

    dispatch(
      setSelectedDays([
        {
          day: formatDateWithTimeZone(wfmAbsence.absenceStartDate),
          hours: wfmAbsence.absenceLength,
          checked: true,
        },
      ])
    )

    setData({
      ...absenceData,
      shiftDescription: wfmAbsence.shiftString,
      partDayAbsence: wfmAbsence.isPartDayAbsence,
      absentFrom: formatDateWithTimeZone(wfmAbsence.absenceStartDate),
      expectedReturn: nextWorkingDay(
        formatDateWithTimeZone(wfmAbsence.absenceStartDate),
        departmentWorksWeekends
      ),
    })
  }, [wfmAbsence])

  const isDayChecked = useCallback(
    (date: Date) => {
      if (departmentSettings?.areWeekendsWorked) {
        return true
      }
      return !isWeekend(date)
    },
    [departmentSettings?.areWeekendsWorked]
  )

  const [isLoadingDays, setIsLoadingDays] = useState<boolean>(false)

  const populateDateRangeShiftLength = useCallback((currentDateRange: NonEmptyDateRange<Date>, employeeId: number) => {
    const currentDate = new Date(currentDateRange[0])
    const endDate = new Date(currentDateRange[1])
    const range: Date[] = []

    const setShiftLengths = async (dates: Date[]) => {
      let foundShift = false

      const allDays = dates.map(async date => {
        const shift = await getShiftLength(employeeId, date)

        setShiftData(shift)
        if (!foundShift && shift?.success) {
          foundShift = true
        }
        if (isEqual(date, dateRange[1]!)) {
          setData((currentAbsenceData) => ({
            ...currentAbsenceData,
            shiftDescription: shift?.desc || '',
            startTime: formatDatTimeWithTimeZone(shift?.startTime || new Date()),
            absenceFrom: formatDatTimeWithTimeZone(shift?.startTime || new Date()),
          }))
        }
        return {
          day: date,
          hours: shift?.hours ? shift.hours : 8,
          checked: isDayChecked(date),
        }
      })

      setIsLoadingDays(true)
      dispatch(setSelectedDays(await Promise.all(allDays)))
      setIsLoadingDays(false)
      setIsDisableShiftDescription(allDays.length === 1 && foundShift)
    }


    while (currentDate <= endDate) {
      range.push(new Date(format(currentDate, DateFormats.DATE_AND_TIME)))
      currentDate.setDate(currentDate.getDate() + 1)
    }
    setShiftLengths(range)
    manualRequestsService
      .getRequestRangeConflicts({
        employeeId,
        dateFrom: currentDateRange[0],
        dateTo: currentDateRange[1]!,
      })
      .then(data => {
        if (data.conflicts.length === 0) {
          setHasConflict(false)
        } else {
          dispatch(setAlerts(data.conflicts))
        }
      })
      .catch(err => {
        if (err?.response?.status === 409) {
          const mappedConflicts = err?.response?.data?.conflicts?.map(
            (conflict: RequestRangeConflict) => ({
              conflictType: conflict.type,
              requestType: conflict.requestTypeName,
              conflictDates: [conflict.date],
            })
          )
          dispatch(setAlerts(mappedConflicts))
          setHasConflict(true)
        }
      })
  }, [getShiftLength, setData, dispatch, setShiftData])

  // Debounce is used here to prevent the premature API requests before user has finished typing the dates
  const debouncedPopulateDateRangeShiftLength = useMemo(
    () => debounce(populateDateRangeShiftLength, 300), [populateDateRangeShiftLength])

  useEffect(() => {
    if (!isLoaded) {
      return
    }

    if (wfmAbsenceId) {
      return
    }

    if (!absenceData.employeeId) {
      setHasConflict(false)
    }

    // no point refetching shift details if dates and employee haven't changed, or employee is invalid
    if ((dateRange === lastShiftFetchParameters.current?.dateRange && lastShiftFetchParameters.current.employeeId === absenceData.employeeId)
        || absenceData.employeeId === 0) {
      return
    }

    // no point refetching shift details if dates are invalid, but we also need to clear selected days
    if (!isValidPlannerDateRange(dateRange)) {
      dispatch(setSelectedDays(null))
      return
    }

    debouncedPopulateDateRangeShiftLength(dateRange, absenceData.employeeId)
    lastShiftFetchParameters.current = { dateRange, employeeId: absenceData.employeeId}

    return () => {
      debouncedPopulateDateRangeShiftLength.cancel()
    }
  }, [
    absenceData.employeeId,
    isLoaded,
    wfmAbsenceId,
    debouncedPopulateDateRangeShiftLength,
    dateRange
  ])


  const updateHours = useCallback(
    (selectedDay: Date, hours: string | null) => {
      if (selectedDays) {
        const updatedDay = selectedDays.find(day => day.day!.getTime() === selectedDay.getTime())
        if (updatedDay) {
          let hoursToFloat = hours ? parseFloat(hours) : 0
          if (hoursToFloat < 0) {
            hoursToFloat = 0.0
          }
          updatedDay.hours = hoursToFloat
          dispatch(setSelectedDays([...selectedDays]))
        }
      }
    },
    [dispatch, selectedDays]
  )

  const updateCheckedDay = (selectedDay: Date, hours: string) => {
    if (selectedDays) {
      const updatedDay = selectedDays.find(day => day.day!.getTime() === selectedDay.getTime())
      if (updatedDay) {
        updatedDay.checked = !updatedDay.checked
        dispatch(setSelectedDays([...selectedDays]))
      }
    }
  }

  useEffect(() => {
    if (stringifyDaysArray(selectedDays) !== stringifyDaysArray(absenceData.days)) {
      updateState('days', selectedDays)
    }
  }, [absenceData.days, selectedDays, updateState])

  function getNextScheduledShift(employeeId: number, date: Date): Promise<Date> {
    return new Promise<Date>((resolve, reject) => {
      requestsService
        .getNextScheduledShift(employeeId, format(new Date(date), 'yyyy-MM-dd'))
        .then(async (data: any) => {
          resolve(new Date(data.nextShift))
        })
        .catch(err => {
          const response: BaseResponse = err.response.data

          if (response.status === 404) {
            resolve(date)
          } else {
            response.errors.forEach(error => {
              dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
            })
          }
        })
    })
  }
  const handleEmployeeChange = async (event: any, employee: Employee | null) => {
    if (employee && isValidPlannerDate(absenceData.endDate)) {
      const expectedReturn: Date = await getNextScheduledShift(
        employee.employeeId,
        new Date(absenceData.endDate!)
      )
      const shift = await getShiftLength(employee.employeeId, new Date(absenceData.endDate!))

      setData({
        ...absenceData,
        employeeId: employee.employeeId,
        shiftDescription: shift?.desc || '',
        partDayAbsence: false,
        expectedReturn,
      })

      setIsEmployeeSelected(true)

      setSelectedEmployeeLocationName(employee.locationGroupName || '')
      setSelectedEmployeeDepartmentId(employee.departmentId)
      setSelectedEmployeeLocationId(employee.locationId || null)
    } else {
      setData({ ...absenceData, employeeId: 0 })
      setIsEmployeeSelected(false)
      setSelectedEmployeeLocationName('')
      setSelectedEmployeeDepartmentId(null)
      setSelectedEmployeeLocationId(null)
    }
  }

  const handleDateRangeChange = async (newValue: DateRange<Date>) => {
    setOpen(false)
    setDateRange(newValue)
    dispatch(setAlerts(null))

    if (newValue[0] && isValidPlannerDate(newValue[0]) && newValue[1] && isValidPlannerDate(newValue[1])) {
      try {
        const expectedReturn: Date = await getNextScheduledShift(
          absenceData.employeeId,
          newValue[1]
        )
        const shiftDescription = await getShiftLength(
          absenceData.employeeId,
          extendAbsenceData ? newValue[1] : newValue[0]
        )

        const newAbsentFrom = createAbsentFromDate(shiftDescription?.startTime ?? newValue[0])

        setData({
          ...absenceData,
          shiftDescription: shiftDescription?.desc || '',
          expectedReturn,
          absentFrom: newAbsentFrom,
          endDate: newValue[1],
          partDayAbsence: false,
        })

      } catch (error) {
        dispatch(showErrorMessage('Get request failed for scheduled shift data and description'))
      }
    }
  }

  const transformRequestBody = (): AbsenceCreateRequest => {
    let baseRequest: AbsenceCreateRequest = {
      absenceId: absenceData.absenceId,
      employeeId: absenceData.employeeId,
      absenceDate:
        typeof absenceData.absentFrom === 'string'
          ? absenceData.absentFrom
          : format(absenceData.absentFrom, DateFormats.DATE_AND_TIME),
      endDate:
        typeof absenceData.endDate === 'string'
          ? absenceData.endDate
          : format(absenceData.endDate, 'yyyy-MM-dd'),
      shiftDescription: absenceData.shiftDescription,
      absenceTypeReasonId: absenceData.absenceCategoryId, // Sickness, non-sickess etc
      absenceTypeId: absenceData.absenceTypeId, // The actual reason (Cold, Paternity, Broken-
      comments: absenceData.comments,
      contactDateTime:
        typeof absenceData.contactDateTime === 'string'
          ? absenceData.contactDateTime
          : format(absenceData.contactDateTime, DateFormats.DATE_AND_TIME),
      contactedByTypeId: absenceData.contactedByTypeId,
      contactComments: absenceData.contactComments,
      expectedReturn:
        typeof absenceData.expectedReturn === 'string'
          ? absenceData.expectedReturn
          : format(absenceData.expectedReturn, 'yyyy-MM-dd'),
      toWorkBack: absenceData.toWorkBack,
      covidIsolationIndicator: absenceData.covidIsolationIndicator,
      sendFDN: absenceData.sendFdn,
      hasHadLunch: absenceData.hasHadLunch || defaultHasHadLunch,
      overrideRequestDays: absenceData.overrideRequestDays,
      days: selectedDays
        ? selectedDays?.map(day => ({
            day: typeof day.day === 'string' ? day.day : format(day.day, DateFormats.Date),
            hours: day.hours,
            checked: day.checked,
          }))
        : [],
      partDayAbsence: absenceData.partDayAbsence,
    }

    if (isManAuthRequest && managerApprovedHours !== undefined) {
      const dpDistributionMap = splitPots && isSickness ? ledgerUnpaidPerDayBeforeDP : exceededHoursPerDay
      const manAuthDays = calculateManAuthDays(
        selectedDays,
        managerApprovedHours,
        dpDistributionMap
      )

      baseRequest = {
        ...baseRequest,
        isManAuthRequested: true,
        manAuthDetails: {
          days: manAuthDays,
          requesterComment: absenceData.comments,
        },
      }
    }
    return baseRequest
  }

  const linkWfmPlaceholderToAbsence = (id: number) => {
    if (!wfmAbsence) {
      return
    }

    const createWfmAbsenceLinkRequest: WFMAbsenceActionedPayload = {
      wfmAbsenceId: wfmAbsence.id,
      absenceId: id,
      actionedBy: userInfo?.name,
    }

    actionsService.wfm.linkWfmPlaceholderToAbsence(createWfmAbsenceLinkRequest).catch(err => {
      dispatch(
        showErrorMessage(
          `An error occured whilst linking WFM Placeholder absence ${wfmAbsence?.id} to absence ${createWfmAbsenceLinkRequest.absenceId}`
        )
      )
    })
  }

  function submitAbsence(conflictOverride?: boolean) {
    setValidationErrors({})
    const errors: AbsenceDrawerValidationErrors = {}
    const isUKBasedAndUKTAOn =
      selectedEmployeeLocationName === LocationGroups.UK && territoryAttendanceUK
    const absenceTypeNotSelected = absenceData.absenceCategoryId === AbsenceTypesEnum.NotSelected
    const absenceReasonTypeNotSelected =
      absenceData.absenceTypeId === AbsenceReasonTypesEnum.NotSelected
    const isNotSicknessAndReasonTypeNotSelected =
      absenceData.absenceCategoryId !== AbsenceTypesEnum.NotSelected && absenceData.absenceCategoryId !== AbsenceTypesEnum.Sickness && absenceReasonTypeNotSelected
    const isReasonSelectedWedding = absenceData.absenceTypeId === AbsenceReasonTypesEnum.Wedding
    const weddingCertificateSubmittedNotChecked =
      isReasonSelectedWedding && absenceData.weddingCertificateSubmitted !== true
    const isReasonSelectedBloodDonation =
      absenceData.absenceTypeId === AbsenceReasonTypesEnum.BloodDonation
    const bloodDonationConfirmNotChecked =
      isReasonSelectedBloodDonation && !absenceData.bloodDonationConfirmed
    const hasNotSelectedReasonType =
      absenceData.absenceTypeId === AbsenceReasonTypesEnum.NotSelected

    if (absenceData.employeeId === 0) {
      errors.employeeId = true
    }

    if (absenceTypeNotSelected) {
      errors.absenceTypeId = true
    }
    if (isUKBasedAndUKTAOn ? isNotSicknessAndReasonTypeNotSelected : hasNotSelectedReasonType) {
      errors.absenceTypeReasonId = true
    }

    if (weddingCertificateSubmittedNotChecked) {
      errors.weddingCertificateSubmitted = true
    }

    if (bloodDonationConfirmNotChecked) {
      errors.bloodDonationConfirmed = true
    }

    if ((isManAuthValidWithHours || extendAbsenceData?.hasManAuthHistory) && absenceData.comments.trim().length === 0) {
      errors.comments = true
    }

    if (expectedReturnDateError || !isValid(absenceData.expectedReturn)) {
      errors.expectedReturn = true
    }

    // Validate contact date time when contact method is selected
    if (absenceData.contactedByTypeId !== 0) {
      if (contactDateTimeError || !isValid(absenceData.contactDateTime)) {
        errors.contactDateTime = true
      }
    }

    if (dateRangeError) {
      errors.dateRange = true
    }

    if (absentFromDateError) {
      errors.absentFromDate = true
    }

    if (Object.keys(errors).length !== 0) {
      setValidationErrors(errors)
      dispatch(showErrorMessage(
        <FormValidationErrorMessage
          message="Form cannot be submitted due to the following errors."
          validationErrors={getValidationErrorMessages(errors)}
        />))
      return
    }

    if (totalRequestedHours === 0) {
      const gridItem = gridContainerRef.current! as any
      gridItem.scrollIntoView()
      dispatch(
        showErrorMessage(
          "Unable to save due to 'Total Hours' must be more than zero hours. Please ensure this is corrected and try again."
        )
      )
      return
    }

    if (hasConflict && !conflictOverride) {
      setShowClashModal(true)
      absenceData.overrideRequestDays = true
      return
    }

    setSubmitLoading(true)

    if (extendAbsenceData) {
      manualRequestsService
        .extendAbsence(transformRequestBody())
        .then(data => {
          setSubmitLoading(false)
          if (data.status === 200 && data.errors.length > 0) {
            data.errors.forEach(error => {
              const { name, description, date } = error
              dispatch(
                showErrorMessage(
                  <UserErrorMessage
                    name={name}
                    description={
                      date ? `${error.description} ${getLocalDateString(date)}` : description
                    }
                  />
                )
              )
            })
          } else {
            dispatch(showSuccessMessage('Extend absence has been successfully updated'))
            dispatch(hideHeaderDrawer())
            dispatch(hideDrawer())
          }

          if (wfmAbsence) {
            linkWfmPlaceholderToAbsence(absenceData.absenceId)

            // Remove Search Params so that the WFM Extend page does not re-open on refresh
            setSearchParams({}, { replace: true })
          }

          if (onAbsenceSubmit) {
            onAbsenceSubmit!()
          }
        })
        .catch(err => {
          setSubmitLoading(false)
          const response: BaseResponse = err.response.data
          response.errors.forEach(error => {
            const { name, description, date } = error
            dispatch(
              showErrorMessage(
                <UserErrorMessage
                  name={name}
                  description={
                    date ? `${description} ${getLocalDateString(date)}` : description
                  }
                />
              )
            )
          })
        })
      return
    }

    if (selectedDays !== null) {
      requestsService
        .createAbsenceRequest(transformRequestBody(), false)
        .then(data => {
          setSubmitLoading(false)
          if (data.status === 200 && data.errors.length > 0) {
            data.errors.forEach(error => {
              const { name, description, date } = error
              dispatch(
                showErrorMessage(
                  <UserErrorMessage
                    name={name}
                    description={
                      date ? `${error.description} ${getLocalDateString(date)}` : description
                    }
                  />
                )
              )
            })
          } else {
            dispatch(showSuccessMessage('Absence request has been sent'))
            dispatch(hideHeaderDrawer())
            dispatch(hideDrawer())
          }
        })
        .catch(err => {
          setSubmitLoading(false)
          const response: BaseResponse = err.response.data
          response.errors.forEach(error => {
            const { name, description, date } = error
            dispatch(
              showErrorMessage(
                <UserErrorMessage
                  name={name}
                  description={
                    date ? `${description} ${getLocalDateString(date)}` : description
                  }
                />
              )
            )
          })
        })
    }
  }

  useEffect(() => {
    setData(current => {
      const { contactDateTime, ...rest } = current
      return { contactDateTime: new Date(), ...rest }
    })
  }, [absenceData.contactedByTypeId])
  
  const populateShiftHours = useCallback(async () => {
  
    if (wfmAbsenceId) {
      return
    }

    if (!selectedDays || selectedDays.length === 0) {
      return
    }

    const firstDayOnly = selectedDays.length > 1 ? [selectedDays[0]] : selectedDays

    await calculatePartDayAbsence(absenceData, firstDayOnly, dispatch, setData, absenceData.absentFrom, selectedDays)
  }, [absenceData, dispatch, selectedDays, setData])

  const onShiftStringBlur = useCallback(
    async (
      e:
        | ChangeEvent<HTMLInputElement & HTMLSelectElement>
        | SelectChangeEvent<string>
        | SelectChangeEvent<number>
        | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      populateShiftHours()
    },
    [populateShiftHours]
  )

  const absenceValidationResult: AbsenceValidationResult = useMemo(() => {
    if (extendAbsenceData) {
      const originalAbsenceDaysCount = extendAbsenceData.absenceDays?.length || 0

      const result = isAbsenceDurationExceededWithExtension(
        absenceData.absenceTypeId,
        absenceData.absenceCategoryId,
        selectedDays,
        extendAbsenceData.startDate,
        originalAbsenceDaysCount
      )

      return result
    }

    const result = isAbsenceDurationExceeded(
      absenceData.absenceTypeId,
      absenceData.absenceCategoryId,
      selectedDays
    )

    return result
  }, [absenceData.absenceTypeId, absenceData.absenceCategoryId, selectedDays, extendAbsenceData])

  useEffect(() => {
    if (absenceData.partDayAbsence) {
      if (savedAbsentFromTimeRef.current && !hasRestoredTimeRef.current) {
        setData(current => ({
          ...current,
          absentFrom: savedAbsentFromTimeRef.current!
        }))
        hasRestoredTimeRef.current = true
        setAbsentFromChangeCounter(prev => prev + 1)
        return
      }
      populateShiftHours()
    } else {
      hasRestoredTimeRef.current = false
      populateShiftHours()
    }
  }, [absenceData.partDayAbsence, absentFromDate, absenceData.hasHadLunch, absentFromChangeCounter])

  useEffect(() => {
    if (!wfmAbsence) {
      return
    }

    if (!selectedDays || selectedDays.length === 0 || !wfmAbsence.isPartDayAbsence) {
      return
    }

    const updatedSelectedDays = selectedDays.map(day => ({
      ...day,
      hours: absenceData.hasHadLunch ? day.hours + 0.5 : Math.max(0, day.hours - 0.5),
    }))

    dispatch(setSelectedDays(updatedSelectedDays))
  }, [absenceData.hasHadLunch])

  useEffect(() => {
    if (!isEmployeeSelected || absenceData.employeeId === 0) {
      return
    }
    manualRequestsService
      .getAbsenceReasonsByEmployeeId(absenceData.employeeId)
      .then(response => {
        setAbsenceTypesList(response.locationAbsenceReasons.options)
        const handbookUrl = response.plannerConfigSettings?.configSettings?.find(
          setting => setting.id === 1
        )?.value
        setEmployeeHandbookUrl(handbookUrl)
      })
      .catch(err => {
        setAbsenceTypesList([])
        setEmployeeHandbookUrl(undefined)
      })
  }, [absenceData.employeeId, isEmployeeSelected])

  useEffect(() => {
    if (!absenceData.partDayAbsence) {
      setData(currentAbsenceData => ({ ...currentAbsenceData, hasHadLunch: defaultHasHadLunch }))
    }
  }, [absenceData.partDayAbsence])

  const showSplitPotsBalanceError =
    isSplitPotsForUk &&
    isSickness &&
    isValidPlannerDateRange(dateRange) &&
    !isLoadingAbsenceOverview &&
    ospBalances.length === 0

  const absenceFormProps: AbsenceFormProps = {
    onShiftStringBlur,
    handleChange,
    handleCheckboxChange,
    popperText,
    isDisableShiftDescription,
    absenceReasonTypeList,
    absenceTypesList,
    open,
    setOpen,
    handleDateChange,
    handleDateError: setAbsenceFromDateError,
    dateRange,
    handleDateRangeChange,
    absenceData,
    extendAbsenceData,
    setData,
    currentTypeAssociatedValueId,
    setCurrentTypeAssociatedValueId,
    validationErrors,
    shiftData,
    setAbsentFromDate,
    setShiftData,
    remainingOSP,
    totalAvailableOSP,
    isLoadingOSP,
    totalRequestedHours,
    exceededHours,
    isPayApprovalConfirmed,
    onDateRangeError: handleDateRangeError,
    setIsPayApprovalConfirmed,
    onApprovalHoursChange: handleApprovalHoursChange,
    approvalHoursValue: managerApprovedHours,
    managerApprovedHours,
    totalSickPayAllowanceHours,
    hasManAuthHistory: extendAbsenceData?.hasManAuthHistory || false,
    hasWorkBackDays,
    employeeHandbookUrl,
    isAbsenceDurationError: absenceValidationResult.isExceeded,
    isAbsenceDateRangeError: dateRangeError,
    absenceErrorMaxDays: absenceValidationResult.maxDays,
    isManAuthRequest,
    isDisableDateRange,
    disabledPartDayAbsence,
    disabledAbsentFrom,
    hasFutureSicknessAbsence,
    onExpectedReturnDateError: setExpectedReturnDateError,
    onContactDateTimeError: setContactDateTimeError,
    absenceOverviewData: enhancedAbsenceOverviewData,
    isLoadingAbsenceOverview,
    ledgerOverviewRows,
    ledgerBreakdownRows,
    isLoadingLedger,
    hasSplitPots: enhancedAbsenceOverviewData?.hasSplitPots,
    splitPots,
    showSplitPotsBalanceError,
  }

  const shouldRender = Boolean(absenceData) && isLoaded

  // NOTE: Do not return early *before* all hooks have been called.
  // This component may render once with `absenceData`/`isLoaded` falsy and then update,
  // and an early return would change the hook order between renders.

  if (!wfmAbsenceId && absenceData) {
    handleAbsentFromChanges(
      open,
      absentFromDate,
      dateRange[0] ?? new Date(),
      setAbsentFromDate,
      absenceData,
      setData
    )
  }

  const handleClashMessage = (clashes: ConflictMatch[]) => {
    const selectedEmployee = allEmployees.find(
      emp => emp.employeeId === absenceData.employeeId
    )?.employeeName

    const requestTypes = clashes.map(clash => clash.requestType)
    const uniqueRequests = requestTypes.filter(
      (value, index, array) => array.indexOf(value) === index
    )

    const conflictDates = clashes.flatMap(clash =>
      getLocalDateString(clash.conflictDates.toString())
    )
    const dates = conflictDates.map(date => `${date}`)

    return `${selectedEmployee} has an existing ${uniqueRequests} request(s) on ${dates
      .join(', ')
      .toString()}. 
      Click Submit to override any existing request(s) on this date(s).`
  }

  const selectedEmployeeLocationResolvedName = useMemo(() => {
    if (!selectedEmployeeLocationId) return undefined
    return employeeAbsenceLocations.find(l => l.locationId === selectedEmployeeLocationId)?.name
  }, [employeeAbsenceLocations, selectedEmployeeLocationId])

  const isWorkBackLocationEligible = useMemo(() => {
    if (!selectedEmployeeLocationResolvedName) return false
    return workBackEligibleLocations.has(selectedEmployeeLocationResolvedName)
  }, [workBackEligibleLocations, selectedEmployeeLocationResolvedName])

  const shouldShowWorkBackConfirmation =
    ukPlannerDayforceAbsence &&
    absenceData.toWorkBack &&
    // The drawer does not have a separate feature toggle for this; tests may pass a toggle
    // but the component is driven purely by location eligibility + ukPlannerDayforceAbsence.
    isWorkBackLocationEligible &&
    !isLoadingEmployeeAbsenceLocations

  useEffect(() => {
    if (!shouldShowWorkBackConfirmation && isWorkBackConfirmed) {
      setIsWorkBackConfirmed(false)
    }
  }, [isWorkBackConfirmed, shouldShowWorkBackConfirmation])

  const { shouldShowSickNoteReminder: sickNoteReminderTriggered } = useSickNoteReminder({
    selectedDays,
    extendAbsenceData,
    isSickness,
    isUKEmployee: selectedEmployeeLocationName === LocationGroups.UK,
  })
  const shouldShowSickNoteReminder = sickNoteReminderTriggered && !hasFutureSicknessAfterEnd

  useEffect(() => {
    if (!shouldShowSickNoteReminder && isSickNoteConfirmed) {
      setIsSickNoteConfirmed(false)
    }
  }, [isSickNoteConfirmed, shouldShowSickNoteReminder])

  const payApprovalContent = useMemo(() => {
    const displayHours = managerApprovedHours !== undefined ? managerApprovedHours : exceededHours ?? 0
    return buildPayApprovalText(displayHours)
  }, [managerApprovedHours, exceededHours])

  const confirmations = useMemo<ConfirmationRow[]>(() => {
    const rows: ConfirmationRow[] = []
    if (shouldShowWorkBackConfirmation) {
      rows.push({
        key: 'work-back',
        content: absenceFormLabels.workBackConfirmationText,
        checked: isWorkBackConfirmed,
        onChange: handleWorkBackConfirmationChange,
        dataTestId: 'MyActions-Absence-WorkBack-Checkbox',
      })
    }
    if (isManAuthValid && isManAuthRequest) {
      rows.push({
        key: 'pay-approval',
        content: payApprovalContent,
        checked: isPayApprovalConfirmed,
        onChange: handlePayApprovalChange,
        dataTestId: 'MyActions-Absence-PayApproval-Checkbox',
      })
    }
    if (shouldShowSickNoteReminder) {
      rows.push({
        key: 'sick-note',
        content: absenceFormLabels.SickNoteReminderText,
        checked: isSickNoteConfirmed,
        onChange: handleSickNoteConfirmationChange,
        dataTestId: 'MyActions-Absence-SickNote-Checkbox',
      })
    }
    return rows
  }, [
    shouldShowWorkBackConfirmation,
    isWorkBackConfirmed,
    isManAuthValid,
    isManAuthRequest,
    isPayApprovalConfirmed,
    handlePayApprovalChange,
    payApprovalContent,
    shouldShowSickNoteReminder,
    isSickNoteConfirmed,
  ])

  const submitDisabled =
    (isManAuthValid && (!isPayApprovalConfirmed || !hasValidHoursInput)) ||
    (shouldShowWorkBackConfirmation && !isWorkBackConfirmed) ||
    (shouldShowSickNoteReminder && !isSickNoteConfirmed) ||
    absenceValidationResult.isExceeded ||
    (absenceData?.absenceCategoryId === 1 && hasFutureSicknessAbsence) ||
    showSplitPotsBalanceError

  if (!shouldRender) {
    return null
  }

  return (
    <Box data-testid="absence-drawer-component">
      <Grid container spacing={4} ref={gridContainerRef}>
        {hasConflict && alerts && (
          <Grid item xs={12}>
            <Alert severity="error" message={handleClashMessage(alerts)} />
          </Grid>
        )}
        <Grid item xs={12} lg={8}>
          <Grid container>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Paragraph weight="bold">{absenceFormLabels.absenceDetail}</Paragraph>
              </Grid>
              {extendAbsenceData ? (
                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Paragraph style={{ display: 'inline-flex' }}>
                    {absenceFormLabels.continuationOfAbsence}
                  </Paragraph>
                  <Paragraph style={{ display: 'inline-flex' }}>
                    {`${getLocalDateString(extendAbsenceData?.startDate)}`}
                  </Paragraph>
                </Grid>
              ) : null}

              {extendAbsenceData ? (
                <Grid item xs={12}>
                  <TextField
                    id="employee"
                    label={absenceFormLabels.employee}
                    value={absenceData?.employeeName}
                    disabled
                    fullWidth
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <AutocompleteList
                    id="employee"
                    label={absenceFormLabels.employee}
                    data={directReports}
                    textField="employeeName"
                    value={directReports.find(emp => emp.employeeId === absenceData.employeeId)}
                    onChange={handleEmployeeChange}
                    error={validationErrors.employeeId}
                    helperText={absenceFormLabels.helperRequired}
                    dataTestId="MyActions-Absence-Employee"
                  />
                  {!isEmployeeSelected && (
                    <Grid item mt={2}>
                      <Alert severity="info" message={absenceFormLabels.selectEmployee} />
                    </Grid>
                  )}
                </Grid>
              )}
              {isEmployeeSelected && (
                <>
                  {territoryAttendanceUK && selectedEmployeeLocationName === LocationGroups.UK && (
                    <UKAbsenceForm {...absenceFormProps} isManAuthOn={isManAuthOn} />
                  )}
                  {!territoryAttendanceUK ||
                    (selectedEmployeeLocationName !== LocationGroups.UK && (
                      <AbsenceForm {...absenceFormProps} />
                    ))}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
        {isEmployeeSelected && (
          <Grid
            item
            xs={12}
            lg={4}
            pl={4}
            sx={{
              marginLeft: 'auto',
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Grid
              container
              direction="column"
              sx={{ height: { xs: 'auto', md: '100%' }, justifyContent: 'space-between' }}
            >
              <Grid item>
                <Box
                  sx={{
                    '& .MuiParagraph-root, & p': {
                      textAlign: 'left !important',
                    },
                  }}
                >
                  <MultiDayRequest
                    type={RequestType.HOLIDAY}
                    cardTitle="Holiday"
                    handleSubmit={handleSubmit}
                    totalRequestedHours={totalRequestedHours}
                    updateHours={updateHours}
                    updateCheckedDay={updateCheckedDay}
                    summaryTitle={absenceFormLabels.occurrenceSummary}
                    summaryOnly
                    isLoading={isLoadingDays}
                    dataTestId="MyActions-Absense"
                  />
                  {hasConflict && (
                    <Box mt={1}>
                      <Paragraph color="#E75E5E" weight="bold">
                        {CLASH_OVERRIDE_MESSAGE}
                      </Paragraph>
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item sx={{ marginTop: '24px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <ConfirmationCard confirmations={confirmations} />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
      <DrawerFooter>
        <Button
          color="secondary"
          label={submitCancelButtons.cancel}
          onClick={() => {
            dispatch(hideHeaderDrawer())
            dispatch(hideDrawer())
          }}
          dataTestId="MyActions-Absence-CancelBtn"
        />
        <Button
          label={submitCancelButtons.submit}
          type="submit"
          onClick={() => submitAbsence()}
          loading={submitLoading}
          dataTestId="MyActions-Absence-SubmitBtn"
          disabled={submitDisabled}
        />
      </DrawerFooter>

      <Modal
        type="clash"
        open={showClashModal}
        onClose={() => {
          setShowClashModal(false)
        }}
        title={absenceFormModalClash.title}
        message={absenceFormModalClash.message}
        buttonLabel={absenceFormModalClash.button}
        onClick={() => {
          setShowClashModal(false)
          submitAbsence(true)
        }}
      />
      <Modal
        type={type}
        open={showModal}
        onClose={() => {
          dispatch(hideModal())
        }}
        title={title}
        message={message}
        buttonLabel={buttonLabel}
      />
    </Box>
  )
}

export default AbsenceDrawerComponent
